/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
        Module No. 17, SBC, Thejaswini Building
        Phase 1, Technopark, Thiruvananthapuram - 695581, 
        Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/


import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { MLTBioBurdenServices } from '../../../service/MLTBioBurden';

interface MLTBioburdenViewProps {
  onBack: () => void;
  testId: string;
  taskData?: any;
}

interface MLTBioburdenPlateApi {
  mapping_id: string;
  plate_id: string;
  serial_number: string;
  batch_number: string | null;
  lot_id: string | null;
  lot_code: string | null;
  plate_type: string | null;
  expiry_date: string | null;
  location_name: string | null;
  operator_name: string | null;
  mapped_at: string | null;
  [key: string]: any;
}

interface MLTBioburdenTaskDetailsApi {
  task_id: string;
  production_schedule_name: string | null;
  product_name: string | null;
  assignee_operator_name: string | null;
  group_test_status: string | null;
  group_test_created_at: string | null;
  group_test_created_by: string | null;
  group_test_updated_at: string | null;
  group_test_updated_by: string | null;
  test_method_name: string | null;
  sample_type_name: string | null;
  sample_id: string | null;
  material_batch_number: string | null;
  production_batch_number: string | null;
  technician_remarks: string | null;
  quantity_sampled: string | number | null;
  quantity_uom_name: string | null;
  test_initiated_at: string | null;
  dilution_required: string | number | boolean | null;
  dilution_factor_name: string | null;
  inoculation_type_id: string | null;
  volume_plated: string | number | null;
  volume_uom: string | null;
  neutralizer_solution_id: string | null;
  neutralizer_solution_name: string | null;
  plates: MLTBioburdenPlateApi[];
  plate_count: number;
  [key: string]: any;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "2026-10-20 15:33:16" -> "20-Oct-2026 03:33 PM" | "2026-11-30" -> "30-Nov-2026"
const formatDateTime = (value?: string | null): string => {
  if (!value) return '';
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (!m) return value;
  const [, y, mo, d, hh, mm] = m;
  const datePart = `${d}-${MONTHS[Number(mo) - 1]}-${y}`;
  if (hh === undefined) return datePart;
  const hour = Number(hh);
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${datePart} ${String(h12).padStart(2, '0')}:${mm} ${hour >= 12 ? 'PM' : 'AM'}`;
};

const capitalize = (value?: string | null): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '';

const toYesNo = (value: string | number | boolean | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '';
  const v = String(value).toLowerCase();
  return v === '1' || v === 'true' || v === 'yes' ? 'Yes' : 'No';
};

const toText = (value: string | number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value);

const MLTBioburdenView: React.FC<MLTBioburdenViewProps> = ({ onBack, testId, taskData }) => {
  const { t } = useTranslation();

  const [apiData, setApiData] = useState<MLTBioburdenTaskDetailsApi | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');

  //api call for MLTBioburden task details
  const getMLTBioburdenTaskDetails = async (testId: string) => {
    try {
      setLoading(true);
      setErrorMessage('');
      const response = await MLTBioBurdenServices.getMLTBioBurdenTaskDetails(testId);

      if (response?.status && response?.statusCode === 200 && response?.data) {
        setApiData(response.data);
      } else {
        setApiData(null);
        setErrorMessage(response?.message || t('mltBioburdenView.messages.fetchFailed', { defaultValue: 'Failed to fetch test details.' }));
      }
    } catch (error) {
      console.error('MLT Bioburden task details error', error);
      setApiData(null);
      setErrorMessage(t('mltBioburdenView.messages.fetchFailed', { defaultValue: 'Failed to fetch test details.' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMLTBioburdenTaskDetails(testId);
  }, [testId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
          {t('mltBioburdenView.messages.loading', { defaultValue: 'Loading...' })}
        </p>
      </div>
    );
  }

  if (!apiData) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4">
        <p className="text-xs font-bold text-slate-500">{errorMessage}</p>
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-xl text-xs font-black text-slate-500 uppercase tracking-widest hover:bg-slate-50 transition-all"
        >
          {t('mltBioburdenView.footer.backToList')}
        </button>
      </div>
    );
  }

  // Same shape as the previous mock data, now populated from the API response.
  // Fields the API does not return yet are left empty and render as "—".
  const apiPlates = apiData.plates || [];
  const statusText = capitalize(apiData.group_test_status);
  const isPending = (apiData.group_test_status || '').toLowerCase() === 'pending';

  const testData = {
    testId: apiData.task_id || taskData?.id || testId,
    activity: apiData.activity_name || 'MLT',
    productionSchedule: apiData.production_schedule_name || '',
    scheduleLine: '',
    productName: apiData.product_name || '',
    productCode: '',
    testMethod: apiData.test_method_name || '', // 'Plate Count' or 'Filtration'
    stage: isPending ? 'Scheduled' : 'Initiated',
    status: statusText,
    initiatedOn: formatDateTime(apiData.test_initiated_at),
    initiatedBy: apiData.assignee_operator_name || '',
    sampleDetails: {
      type: apiData.sample_type_name || '',
      id: apiData.sample_id || '',
      batchNo: apiData.production_batch_number || apiData.material_batch_number || '',
      quantity: toText(apiData.quantity_sampled),
      uom: apiData.quantity_uom_name || '',
      location: apiPlates[0]?.location_name || ''
    },
    prepDetails: {
      dilutionRequired: toYesNo(apiData.dilution_required),
      dilutionFactor: apiData.dilution_factor_name || '',
      neutralizerRequired: apiData.neutralizer_solution_id ? 'Yes' : (apiData.neutralizer_solution_id === null ? '' : 'No'),
      diluentType: apiData.neutralizer_solution_name || '',
      preparedBy: '',
      preparedOn: ''
    },
    inoculationDetails: {
      type: toText(apiData.inoculation_type_id),
      volumePlated: toText(apiData.volume_plated),
      uom: apiData.volume_uom || '',
      numPlates: apiData.plate_count ?? apiPlates.length,
      dilutionSeries: ''
    },
    plates: apiPlates.map((p) => ({
      serialNo: p.serial_number,
      id: p.plate_id,
      mediaType: p.plate_type || '',
      size: '',
      lotNo: p.lot_code || '',
      expiry: formatDateTime(p.expiry_date),
      status: capitalize(p.status),
      selectedOn: formatDateTime(p.mapped_at),
      selectedBy: p.operator_name || ''
    })),
    remarks: apiData.technician_remarks || '',
    // Built from the audit timestamps available on the response
    history: [
      apiData.group_test_created_at && {
        stage: 'Draft',
        action: 'Test Created',
        by: apiData.assignee_operator_name || '',
        time: formatDateTime(apiData.group_test_created_at),
        remarks: '-'
      },
      apiData.group_test_updated_at && apiData.group_test_updated_at !== apiData.group_test_created_at && {
        stage: 'Draft',
        action: 'Test Updated',
        by: apiData.assignee_operator_name || '',
        time: formatDateTime(apiData.group_test_updated_at),
        remarks: '-'
      },
      apiData.test_initiated_at && {
        stage: 'Initiated',
        action: 'Test Initiated',
        by: apiData.assignee_operator_name || '',
        time: formatDateTime(apiData.test_initiated_at),
        remarks: '-'
      }
    ].filter(Boolean) as { stage: string; action: string; by: string; time: string; remarks: string }[]
  };


  const isPlateCount = testData.testMethod === 'Plate Count';

  const DetailItem = ({ label, value }: { label: string; value: string }) => (
    <div className="space-y-1">
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
      <p className="text-[12px] font-bold text-slate-700">{value || '—'}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-white media-prep-scope">
      {/* Header */}
      <div className="bg-[#1E293B] p-6 text-white border-b border-white/10">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <button 
                onClick={onBack}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
              >
                <span className="material-symbols-outlined text-sm">arrow_back</span>
              </button>
              <h1 className="text-xl font-black tracking-tight">{t("mltBioburdenView.header.title")}</h1>
            </div>
            
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.header.testId")}</span>
                <span className="text-[11px] font-bold text-blue-400">{testData.testId}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.header.schedule")}</span>
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 font-mono font-bold rounded text-[10px]">
                  {testData.productionSchedule}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.header.product")}</span>
                <span className="text-[11px] font-bold text-white">{testData.productName}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.header.method")}</span>
                <span className="text-[11px] font-bold">{testData.testMethod}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.header.stage")}</span>
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded text-[9px] font-black uppercase tracking-widest">
                  {testData.stage}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.header.status")}</span>
                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded text-[9px] font-black uppercase tracking-widest">
                  {testData.status}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-4 text-right">
            <div className="flex gap-4">
              <div className="flex flex-col items-end">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.header.initiatedOn")}</span>
                <span className="text-[11px] font-bold">{testData.initiatedOn}</span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.header.initiatedBy")}</span>
                <span className="text-[11px] font-bold">{testData.initiatedBy}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all">
                {t("mltBioburdenView.header.edit")}
              </button>
              <button className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-all">
                <span className="material-symbols-outlined text-lg">more_vert</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-700">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Section 1: Sample & Mapping Details */}
          <div className="space-y-4">
            <h3 className="text-[11px] font-black text-blue-600 uppercase tracking-[0.2em] flex items-center gap-2">
              <span className="w-1.5 h-4 bg-blue-600 rounded-full"></span>
              {t("mltBioburdenView.sections.sampleMapping")}
            </h3>
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-2 gap-y-6 gap-x-4">
              <DetailItem label={t("mltBioburdenView.fields.mappedProductionSchedule")} value={testData.scheduleLine ? `${testData.productionSchedule} (${testData.scheduleLine})` : testData.productionSchedule} />
              <DetailItem label={t("mltBioburdenView.fields.productNameCode")} value={testData.productCode ? `${testData.productName} [${testData.productCode}]` : testData.productName} />
              <DetailItem label={t("mltBioburdenView.fields.sampleType")} value={testData.sampleDetails.type} />
              <DetailItem label={t("mltBioburdenView.fields.sampleId")} value={testData.sampleDetails.id} />
              <DetailItem label={t("mltBioburdenView.fields.batchNo")} value={testData.sampleDetails.batchNo} />
              <DetailItem label={t("mltBioburdenView.fields.sampleQuantity")} value={testData.sampleDetails.quantity} />
              <DetailItem label={t("mltBioburdenView.fields.uom")} value={testData.sampleDetails.uom} />
              <DetailItem label={t("mltBioburdenView.fields.sourceLocation")} value={testData.sampleDetails.location} />
            </div>
          </div>

          {/* Section 2: Preparation Details */}
          <div className="space-y-4">
            <h3 className="text-[11px] font-black text-blue-600 uppercase tracking-[0.2em] flex items-center gap-2">
              <span className="w-1.5 h-4 bg-blue-600 rounded-full"></span>
              {t("mltBioburdenView.sections.preparation")}
            </h3>
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-2 gap-y-6 gap-x-4">
              <DetailItem label={t("mltBioburdenView.fields.dilutionRequired")} value={testData.prepDetails.dilutionRequired} />
              <DetailItem label={t("mltBioburdenView.fields.dilutionFactor")} value={testData.prepDetails.dilutionRequired === 'Yes' ? testData.prepDetails.dilutionFactor : t("mltBioburdenView.fields.notApplicable")} />
              <DetailItem label={t("mltBioburdenView.fields.neutralizerRequired")} value={testData.prepDetails.neutralizerRequired} />
              <DetailItem label={t("mltBioburdenView.fields.diluentNeutralizerType")} value={testData.prepDetails.diluentType} />
              <DetailItem label={t("mltBioburdenView.fields.preparedBy")} value={testData.prepDetails.preparedBy} />
              <DetailItem label={t("mltBioburdenView.fields.preparedOn")} value={testData.prepDetails.preparedOn} />
            </div>
          </div>

          {/* Section 3: Method Specific */}
          {isPlateCount ? (
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-[11px] font-black text-blue-600 uppercase tracking-[0.2em] flex items-center gap-2">
                  <span className="w-1.5 h-4 bg-blue-600 rounded-full"></span>
                  {t("mltBioburdenView.sections.plateSelection")}
                </h3>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.plateSelection.required")}</span>
                    <span className="text-[11px] font-black text-blue-600">{testData.inoculationDetails.numPlates}</span>
                  </div>
                  <div className="flex items-center gap-2 bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-100">
                    <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">{t("mltBioburdenView.plateSelection.selected")}</span>
                    <span className="text-[11px] font-black text-emerald-600">{testData.plates.length}</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest w-16">{t("mltBioburdenView.plateTable.index")}</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.plateTable.serialNo")}</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.plateTable.plateId")}</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.plateTable.mediaType")}</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.plateTable.lotNo")}</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.plateTable.expiry")}</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.plateTable.selectedBy")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {testData.plates.map((plate, idx) => (
                      <tr key={plate.id} className="hover:bg-slate-50/50 transition-all">
                        <td className="px-6 py-4 text-xs font-bold text-slate-400">{idx + 1}</td>
                        <td className="px-6 py-4 text-xs font-black text-blue-600">{plate.serialNo}</td>
                        <td className="px-6 py-4 text-xs font-bold text-slate-700">{plate.id}</td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-[10px] font-black">{plate.mediaType || '—'}</span>
                        </td>
                        <td className="px-6 py-4 text-xs font-bold text-slate-600">{plate.lotNo}</td>
                        <td className="px-6 py-4 text-xs font-bold text-slate-500">{plate.expiry}</td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-[11px] font-bold text-slate-700">{plate.selectedBy}</span>
                            <span className="text-[9px] font-bold text-slate-400">{plate.selectedOn}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {/* Section 4: Inoculation Details (Plate Count) */}
          {isPlateCount && (
            <div className="lg:col-span-2 space-y-4">
              <h3 className="text-[11px] font-black text-blue-600 uppercase tracking-[0.2em] flex items-center gap-2">
                <span className="w-1.5 h-4 bg-blue-600 rounded-full"></span>
                {t("mltBioburdenView.sections.inoculation")}
              </h3>
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-1 md:grid-cols-5 gap-6">
                <DetailItem label={t("mltBioburdenView.fields.inoculationType")} value={testData.inoculationDetails.type} />
                <DetailItem label={t("mltBioburdenView.fields.volumePlated")} value={testData.inoculationDetails.volumePlated ? `${testData.inoculationDetails.volumePlated} ${testData.inoculationDetails.uom}`.trim() : ''} />
                <DetailItem label={t("mltBioburdenView.fields.numberOfPlates")} value={testData.inoculationDetails.numPlates.toString()} />
                <DetailItem label={t("mltBioburdenView.fields.dilutionSeries")} value={testData.inoculationDetails.dilutionSeries} />
              </div>
            </div>
          )}

          {/* Section 5: Remarks */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-[11px] font-black text-blue-600 uppercase tracking-[0.2em] flex items-center gap-2">
              <span className="w-1.5 h-4 bg-blue-600 rounded-full"></span>
              {t("mltBioburdenView.sections.remarks")}
            </h3>
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <p className="text-xs font-bold text-slate-600 leading-relaxed italic">
                {testData.remarks ? `"${testData.remarks}"` : '—'}
              </p>
            </div>
          </div>

          {/* Section 6: Audit Trail */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-[11px] font-black text-blue-600 uppercase tracking-[0.2em] flex items-center gap-2">
              <span className="w-1.5 h-4 bg-blue-600 rounded-full"></span>
              {t("mltBioburdenView.sections.auditTrail")}
            </h3>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.historyTable.stage")}</th>
                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.historyTable.action")}</th>
                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.historyTable.performedBy")}</th>
                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.historyTable.dateTime")}</th>
                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenView.historyTable.remarks")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {testData.history.map((entry, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50 transition-all">
                      <td className="px-6 py-4">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-[9px] font-black uppercase tracking-widest border border-slate-200">
                          {entry.stage}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs font-black text-slate-700">{entry.action}</td>
                      <td className="px-6 py-4 text-xs font-bold text-slate-600">{entry.by}</td>
                      <td className="px-6 py-4 text-[11px] font-bold text-slate-400">{entry.time}</td>
                      <td className="px-6 py-4 text-xs font-bold text-slate-500 italic">{entry.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-slate-100 p-6 flex items-center justify-between sticky bottom-0 z-10 shadow-[0_-1px_10px_rgba(0,0,0,0.02)]">
        <button 
          onClick={onBack}
          className="px-6 py-3 rounded-xl text-xs font-black text-slate-500 uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-lg">arrow_back</span>
          {t("mltBioburdenView.footer.backToList")}
        </button>
        
        <div className="flex gap-4">
          <button className="px-6 py-3 bg-white border border-slate-200 rounded-xl text-xs font-black text-slate-600 uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center gap-2">
            <span className="material-symbols-outlined text-lg">print</span>
            {t("mltBioburdenView.footer.print")}
          </button>
          <button className="px-6 py-3 bg-white border border-slate-200 rounded-xl text-xs font-black text-slate-600 uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center gap-2">
            <span className="material-symbols-outlined text-lg">history</span>
            {t("mltBioburdenView.footer.history")}
          </button>
          <button className="px-10 py-3 bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 active:scale-95">
            {t("mltBioburdenView.footer.nextStage")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MLTBioburdenView;